import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../services/api";
import { GLOBAL_ADMIN_CSS, COLORS } from "./adminStyles";

const AdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [mode, setMode] = useState("LOGIN");
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const navigate = useNavigate();

  // Redirect if already logged in
  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (token) navigate("/winrox11-super-admin/dashboard");
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError("Email is required."); return; }
    if (!password || password.length < 6) { setError("Password must be at least 6 characters."); return; }
    setError("");
    setLoading(true);
    try {
      if (mode === "LOGIN") {
        const res = await API.post(`/admin/login`, { email: email.trim().toLowerCase(), password });
        localStorage.setItem("adminToken", res.data.token);
        navigate("/winrox11-super-admin/dashboard");
      } else {
        const res = await API.post(`/admin/register`, { email: email.trim().toLowerCase(), password });
        setError("");
        setMode("LOGIN");
        setPassword("");
        // Show success via a temp banner
        setError("✅ " + (res.data.msg || "Admin registered. Please login."));
      }
    } catch (err) {
      setError(err.response?.data?.msg || `${mode === "LOGIN" ? "Login" : "Registration"} failed. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const isSuccess = error.startsWith("✅");

  return (
    <div style={{
      height: "100vh",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      background: COLORS.bg,
      fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
      position: "relative",
      overflow: "hidden",
    }}>
      <style>{GLOBAL_ADMIN_CSS}</style>

      {/* Background glow effects */}
      <div style={{ position: "absolute", top: "-100px", left: "50%", transform: "translateX(-50%)", width: "600px", height: "600px", background: `radial-gradient(circle, ${COLORS.indigo}08 0%, transparent 70%)`, pointerEvents: "none" }} />

      <div style={{
        background: COLORS.card,
        padding: "44px 40px",
        borderRadius: "24px",
        border: `1px solid ${COLORS.cardBorder}`,
        width: "100%",
        maxWidth: "420px",
        textAlign: "center",
        boxShadow: "0 24px 80px rgba(0,0,0,0.5)",
        position: "relative",
      }}>
        {/* Logo */}
        <div style={{
          width: "64px",
          height: "64px",
          borderRadius: "18px",
          background: COLORS.indigo + "15",
          border: `1px solid ${COLORS.indigo}30`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "28px",
          margin: "0 auto 20px",
        }}>
          🏏
        </div>

        <div style={{ fontSize: "28px", fontWeight: 800, color: COLORS.text, letterSpacing: "-0.5px", marginBottom: "4px" }}>
          WinroX<span style={{ color: COLORS.indigo }}>11</span>
        </div>
        <div style={{ fontSize: "10px", fontWeight: 700, color: COLORS.textMuted, letterSpacing: "3px", textTransform: "uppercase", marginBottom: "28px" }}>
          Super Admin Panel
        </div>

        {/* Toggle */}
        <div style={{ display: "flex", gap: "4px", marginBottom: "24px", background: COLORS.inputBg, borderRadius: "12px", padding: "4px", border: `1px solid ${COLORS.cardBorder}` }}>
          {["LOGIN", "REGISTER"].map(m => (
            <button
              key={m}
              onClick={() => { setMode(m); setError(""); }}
              style={{
                flex: 1,
                padding: "10px",
                background: mode === m ? COLORS.indigo : "transparent",
                color: mode === m ? "white" : COLORS.textMuted,
                border: "none",
                borderRadius: "10px",
                cursor: "pointer",
                fontWeight: 700,
                fontSize: "11px",
                letterSpacing: "1px",
                textTransform: "uppercase",
                transition: "all 0.2s",
                fontFamily: "'Inter', sans-serif",
              }}
            >
              {m === "LOGIN" ? "Login" : "Create Admin"}
            </button>
          ))}
        </div>

        {error && (
          <div style={{
            background: isSuccess ? COLORS.green + "15" : COLORS.red + "15",
            border: `1px solid ${isSuccess ? COLORS.green + "30" : COLORS.red + "30"}`,
            borderRadius: "10px",
            padding: "12px 14px",
            marginBottom: "16px",
            fontSize: "12px",
            color: isSuccess ? COLORS.green : "#fca5a5",
            fontWeight: 600,
            textAlign: "left",
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <div>
            <input
              className="admin-input-focus"
              type="email"
              placeholder="admin@email.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              style={{
                width: "100%",
                padding: "14px 18px",
                background: COLORS.inputBg,
                border: `1px solid ${COLORS.cardBorder}`,
                borderRadius: "12px",
                color: COLORS.text,
                fontSize: "14px",
                outline: "none",
                boxSizing: "border-box",
                fontFamily: "'Inter', sans-serif",
                transition: "border-color 0.2s",
              }}
            />
          </div>
          <div style={{ position: "relative" }}>
            <input
              className="admin-input-focus"
              type={showPass ? "text" : "password"}
              placeholder="••••••••"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              style={{
                width: "100%",
                padding: "14px 48px 14px 18px",
                background: COLORS.inputBg,
                border: `1px solid ${COLORS.cardBorder}`,
                borderRadius: "12px",
                color: COLORS.text,
                fontSize: "14px",
                outline: "none",
                boxSizing: "border-box",
                fontFamily: "'Inter', sans-serif",
                transition: "border-color 0.2s",
              }}
            />
            <button
              type="button"
              onClick={() => setShowPass(!showPass)}
              style={{
                position: "absolute",
                right: "14px",
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                color: COLORS.textMuted,
                cursor: "pointer",
                fontSize: "16px",
                padding: 0,
              }}
            >
              {showPass ? "🙈" : "👁️"}
            </button>
          </div>
          <button
            type="submit"
            disabled={loading}
            className={loading ? "" : "admin-btn-hover"}
            style={{
              padding: "14px",
              background: loading ? COLORS.cardBorder : COLORS.indigo,
              color: "white",
              border: "none",
              borderRadius: "12px",
              fontSize: "14px",
              cursor: loading ? "not-allowed" : "pointer",
              fontWeight: 700,
              letterSpacing: "1px",
              textTransform: "uppercase",
              marginTop: "4px",
              opacity: loading ? 0.7 : 1,
              transition: "all 0.15s",
              fontFamily: "'Inter', sans-serif",
            }}
          >
            {loading ? "Processing..." : (mode === "LOGIN" ? "🔓 Enter Dashboard" : "✅ Register Admin")}
          </button>
        </form>

        <div style={{ fontSize: "11px", color: COLORS.textDim, marginTop: "20px" }}>
          Secure · JWT Authenticated · Admin Only
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
